// src/finance/targets.ts
// Objectifs mensuels du suivi (spec § 3) : dérivés de l'état de simulation
// courant, régime MIX (le levier réellement piloté par Léo).
import type { FinanceSettings } from '../lib/types'
import { simulateRegime } from './engine/engine'
import { findRevenueForNet } from './engine/invert'
import { round2 } from './engine/tax'

export type MonthlyTargets = {
  revenue: number
  net: number
  expenses: number
  /**
   * true quand aucun CA ne permet d'atteindre le net visé avec ces paramètres
   * fiscaux (findRevenueForNet a rendu null). `revenue` et `net` valent alors 0 :
   * l'affichage doit annoncer « cible inatteignable » plutôt qu'un montant.
   */
  unreachable: boolean
}

/** Objectifs réellement opposés à un mois donné, + d'où ils viennent. */
export type EffectiveTargets = MonthlyTargets & {
  /**
   * true : objectifs FIGÉS à la création du mois (migration 0012).
   * false : le mois est antérieur au gel, on lui oppose les objectifs COURANTS —
   * l'affichage doit le signaler, sans quoi un mois clos est jugé sur une cible
   * qui n'existait pas encore.
   */
  frozen: boolean
}

/**
 * Objectifs figés du mois s'il en a, objectifs courants sinon.
 *
 * Le gel est TOUT ou RIEN : trois objectifs figés ou aucun. Un gel partiel
 * mélangerait deux échelles (un CA cible d'août face à un net cible d'aujourd'hui),
 * ce qui produirait des deltas incohérents entre eux ; on repart alors des
 * objectifs courants, qui sont au moins mutuellement cohérents.
 * `0` est un objectif figé valide — seul `null` signifie « pas d'objectif ».
 */
export function effectiveTargetsFor(
  month: { targetRevenue: number | null; targetNet: number | null; targetExpenses: number | null },
  current: MonthlyTargets,
): EffectiveTargets {
  const { targetRevenue, targetNet, targetExpenses } = month
  if (targetRevenue === null || targetNet === null || targetExpenses === null) {
    return { ...current, frozen: false }
  }
  return {
    revenue: targetRevenue,
    net: targetNet,
    expenses: targetExpenses,
    // Un objectif figé est un montant déjà calculé : il n'y a plus d'inversion à
    // tenter, donc jamais d'« inatteignable » — même si les paramètres fiscaux
    // actuels rendent la cible courante hors de portée.
    unreachable: false,
    frozen: true,
  }
}

/**
 * Part salaire du scénario RÉELLEMENT sélectionné dans le calculateur.
 *
 * `payerMode` est persisté depuis la mémorisation de « Qui paie quoi » : les
 * objectifs du Suivi doivent en découler, sinon un utilisateur 100 % dividendes
 * se voit opposer un objectif de CA calculé sur le mix — l'écart tient aux
 * cotisations salariales, pas à sa performance.
 */
function shareOf(settings: FinanceSettings): number {
  if (settings.payerMode === 'fullSalary') return 1
  if (settings.payerMode === 'fullDividends') return 0
  return settings.mixSalaryShare
}

/**
 * Scénario réellement retenu pour les objectifs, en clair : « 100 % salaire, UK ».
 *
 * Le bandeau du Suivi portait « (mix de la simulation) » en dur alors que S4 avait
 * rendu les objectifs dépendants de `payerMode` et `payerRegime` : en 100 % salaire
 * il annonçait 8 787,83 € « du mix », lequel vaut 7 574,15 € — 1 213,68 €/mois
 * d'écart entre le chiffre affiché et l'étiquette qui le décrivait.
 */
export function targetsScenarioLabel(settings: FinanceSettings): string {
  const scenario =
    settings.payerMode === 'fullSalary'
      ? '100 % salaire'
      : settings.payerMode === 'fullDividends'
        ? '100 % dividendes'
        : 'mix'
  return `${scenario}, ${settings.payerRegime === 'uk' ? 'UK' : 'France'}`
}

export function computeMonthlyTargets(
  settings: FinanceSettings,
  companyExpensesMonthly: number,
): MonthlyTargets {
  const share = shareOf(settings)
  // Même juridiction que celle affichée par le calculateur : l'IS français et la
  // Corporation Tax UK ne donnent pas le même CA cible (~180 €/mois d'écart).
  const regimeJurisdiction = settings.payerRegime
  const revenue =
    settings.direction === 'netToRevenue'
      ? findRevenueForNet(
          settings.targetAmount,
          share,
          settings,
          companyExpensesMonthly,
          regimeJurisdiction,
        )
      : settings.targetAmount
  // findRevenueForNet rend null quand aucun CA n'atteint la cible avec ces
  // paramètres (C5) : on ne propage pas un nombre absurde dans le Suivi.
  if (revenue === null) {
    return { revenue: 0, net: 0, expenses: round2(companyExpensesMonthly), unreachable: true }
  }
  const regime = simulateRegime(revenue, share, settings, companyExpensesMonthly, regimeJurisdiction)
  return {
    revenue: regime.revenueMonthly,
    net: regime.netMonthly,
    expenses: regime.breakdown.companyExpenses,
    unreachable: false,
  }
}
